/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link, Navigate, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import FloatSemanticMessage from "components/FloatSemanticMessage";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import {
  setAuthPersistence,
  signUpWithEmailAndPasswordFirebase,
  subscribeToAuthChanges,
} from "services/firebase";
import { syncAuthenticatedUser } from "services/users";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function Cover() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setAuthenticatedUser(user);
    });

    return unsubscribe;
  }, []);

  const resolveAuthErrorMessage = (error) => {
    const authCode = error?.code;

    switch (authCode) {
      case "auth/email-already-in-use":
        return "Este e-mail ja esta em uso.";
      case "auth/invalid-email":
        return "O e-mail informado e invalido.";
      case "auth/weak-password":
        return "A senha precisa ter pelo menos 6 caracteres.";
      default:
        return error?.message || "Nao foi possivel criar sua conta agora. Tente novamente.";
    }
  };

  const validateFields = () => {
    if (!name.trim()) {
      setErrorMessage("Informe seu nome.");
      return false;
    }

    if (!email.trim()) {
      setErrorMessage("Informe seu e-mail.");
      return false;
    }

    if (!password) {
      setErrorMessage("Informe sua senha.");
      return false;
    }

    if (password.length < 6) {
      setErrorMessage("A senha precisa ter pelo menos 6 caracteres.");
      return false;
    }

    if (!acceptedTerms) {
      setErrorMessage("Voce precisa aceitar os termos para continuar.");
      return false;
    }

    return true;
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!validateFields()) return;

    setIsLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      await setAuthPersistence();
      const userCredential = await signUpWithEmailAndPasswordFirebase(
        name.trim(),
        normalizedEmail,
        password
      );
      await syncAuthenticatedUser(userCredential.user, {
        email: normalizedEmail,
        fullName: name.trim(),
      });
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(resolveAuthErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (authenticatedUser && !isLoading && !errorMessage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <FloatSemanticMessage
        open={Boolean(errorMessage)}
        title="Falha no cadastro"
        message={errorMessage || ""}
        severity="error"
        onClose={() => setErrorMessage("")}
      />
      <BasicLayout image={bgImage}>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              Crie sua conta
            </MDTypography>
            <MDTypography display="block" variant="button" color="white" my={1}>
              Preencha seus dados para continuar
            </MDTypography>
          </MDBox>
          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleSignUp}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Nome"
                  variant="standard"
                  fullWidth
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="E-mail"
                  variant="standard"
                  fullWidth
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Senha"
                  variant="standard"
                  fullWidth
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
              <MDBox display="flex" alignItems="center" ml={-1}>
                <Checkbox
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Eu concordo com os&nbsp;
                </MDTypography>
                <MDTypography
                  component="a"
                  href="#"
                  variant="button"
                  fontWeight="bold"
                  color="info"
                  textGradient
                >
                  Termos e Condicoes
                </MDTypography>
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={18} color="inherit" /> : "Cadastrar"}
                </MDButton>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  Ja tem uma conta?{" "}
                  <MDTypography
                    component={Link}
                    to="/login"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Entrar
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </BasicLayout>
    </>
  );
}

export default Cover;
